// DistributedSystemsTheoryDetail.jsx — Left TOC + scroll-spy + RIGHT Sponsored
// (Bootstrap + React Router v4/v5)
// Updated: Template/Example cards match your screenshot style (badge on right, not “[Template]” text)
// Also: richer + more readable content, and Facebook-like Home Feed examples in EVERY section.

import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

/* ================== AdSense helpers ================== */
const ADSENSE_CLIENT = "ca-pub-9033879555371063";
const ADS_ENABLED = (process.env.REACT_APP_ENABLE_ADS ?? "true") === "true";

function useAdSense(clientId, enabled) {
  useEffect(() => {
    if (!enabled || !clientId) return;

    const existing = document.querySelector("script[data-adsbygoogle]");
    if (!existing) {
      const s = document.createElement("script");
      s.setAttribute("data-adsbygoogle", "true");
      s.async = true;
      s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${clientId}`;
      s.crossOrigin = "anonymous";
      document.head.appendChild(s);
    } else {
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
      } catch {}
    }
  }, [clientId, enabled]);
}

function AdSlot({
  adSlot = "5201219465",
  format = "auto",
  fullWidthResponsive = "true",
  style = { display: "block", minHeight: 90 },
  className = "",
  enabled = true,
}) {
  useEffect(() => {
    if (!enabled) return;
    try {
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch {}
  }, [enabled]);

  if (!enabled) return null;

  return (
    <ins
      className={`adsbygoogle ${className}`}
      style={style}
      data-ad-client={ADSENSE_CLIENT}
      data-ad-slot={adSlot}
      data-ad-format={format}
      data-full-width-responsive={fullWidthResponsive}
    />
  );
}

function RightSponsoredCard() {
  return (
    <div className="card border-0 shadow-sm">
      <div className="card-body">
        <h3 className="h6 text-uppercase text-muted mb-2">Sponsored</h3>
        <AdSlot enabled={ADS_ENABLED} adSlot="5201219465" />
        <div className="small text-muted mt-2">Ads help keep content free.</div>
      </div>
    </div>
  );
}

/* ================== small UI helpers ================== */
const Section = ({ id, title, children }) => (
  <section id={id} className="mb-5">
    <h2 className="h4 mb-2">{title}</h2>
    {children}
    <div className="mt-3">
      <a href="#top" className="small text-muted">
        ↑ Back to top
      </a>
    </div>
  </section>
);

function BadgeRight({ children, tone = "secondary" }) {
  return (
    <span className={`badge bg-${tone} text-white`} style={{ whiteSpace: "nowrap" }}>
      {children}
    </span>
  );
}

// Card that looks like your screenshot: title left, badge on right.
function LabeledCard({ label = "Template", tone = "secondary", title, children }) {
  return (
    <div className="card border-0 shadow-sm mt-3">
      <div className="card-body">
        <div className="d-flex align-items-start justify-content-between gap-3 mb-2">
          <div className="fw-semibold">{title}</div>
          <BadgeRight tone={tone}>{label}</BadgeRight>
        </div>
        <div className="text-muted" style={{ whiteSpace: "pre-wrap" }}>
          {children}
        </div>
      </div>
    </div>
  );
}

const TemplateCard = ({ title = "Template", children }) => (
  <LabeledCard label="Template" tone="secondary" title={title}>
    {children}
  </LabeledCard>
);

const ExampleCard = ({ title = "Facebook-like Home Feed — example", children }) => (
  <LabeledCard label="Example" tone="success" title={title}>
    {children}
  </LabeledCard>
);

const CodeCard = ({ title = "Template — pseudo-code", children }) => (
  <div className="card border-0 shadow-sm mt-3">
    <div className="card-body">
      <div className="d-flex align-items-start justify-content-between gap-3 mb-2">
        <div className="fw-semibold">{title}</div>
        <BadgeRight tone="secondary">Template</BadgeRight>
      </div>
      <pre
        className="bg-light rounded p-3 border small mb-0"
        style={{ whiteSpace: "pre-wrap", overflowX: "auto" }}
      >
        <code>{children}</code>
      </pre>
    </div>
  </div>
);

// Optional “Say …” callout (like your blue strip)
function SayCallout({ children }) {
  return (
    <div className="alert alert-primary d-flex align-items-center mb-3" role="alert">
      <div className="me-2 fw-semibold">Say</div>
      <div className="mb-0">{children}</div>
    </div>
  );
}

const TOC = [
  { id: "cap-pacelc", label: "A) CAP, PACELC, consistency models" },
  { id: "consensus", label: "B) Consensus & coordination" },
  { id: "leader-fence", label: "C) Leader election & fencing" },
  { id: "clocks", label: "D) Clocks & ordering" },
  { id: "conflicts", label: "E) Conflict resolution & CRDTs" },
  { id: "mini-case", label: "Mini stance: multi-region settings" },
  { id: "pitfalls", label: "Common pitfalls & fixes" },
  { id: "oneliners", label: "One-liners for interviews" },
];

/* ================== page ================== */
export default function DistributedSystemsTheoryDetail() {
  useAdSense(ADSENSE_CLIENT, ADS_ENABLED);

  const [activeId, setActiveId] = useState(TOC[0].id);

  // scroll-spy
  useEffect(() => {
    const opts = { root: null, rootMargin: "0px 0px -60% 0px", threshold: 0.1 };
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) setActiveId(e.target.id);
      });
    }, opts);

    TOC.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) obs.observe(el);
    });

    return () => obs.disconnect();
  }, []);

  // One consistent running example
  const feed = useMemo(
    () => ({
      name: "Facebook-like Home Feed",
      freshness: "1–10s",
      partitionKeys: ["user_id", "post_id", "creator_id"],
      regions: ["us-east", "us-west", "eu"],
    }),
    []
  );

  return (
    <div className="container-lg py-4" id="top">
      {/* breadcrumb */}
      <nav className="small mb-2">
        <Link to="/systemDesign">System Design</Link>
        <span className="text-muted"> / </span>
        <span>Distributed systems theory</span>
      </nav>

      {/* header */}
      <header
        className="mb-4 text-center rounded-3 p-3 p-md-4"
        style={{
          background: "linear-gradient(135deg, rgba(25,135,84,.07), rgba(108,117,125,.06))",
        }}
      >
        <div>
          <span className="badge bg-dark text-white mb-2">System Design</span>
          <h1 className="h3 mb-1">7) Distributed systems theory — detailed guide</h1>
          <p className="text-muted mb-0">
            CAP &amp; PACELC, consensus (Raft/Paxos), leader election + fencing, clocks &amp; ordering,
            CRDTs — with a {feed.name} example in every section.
          </p>
        </div>
      </header>

      {/* layout */}
      <div className="row g-4">
        {/* LEFT TOC */}
        <aside className="col-12 col-xl-2 d-print-none">
          <div className="position-sticky" style={{ top: "1rem" }}>
            <div className="card border-0 shadow-sm">
              <div className="card-body">
                <h3 className="h6 text-uppercase text-muted mb-2">On this page</h3>
                <nav className="nav flex-column small">
                  {TOC.map(({ id, label }) => (
                    <a
                      key={id}
                      href={`#${id}`}
                      className={`nav-link px-0 ${activeId === id ? "fw-semibold text-success" : ""}`}
                      aria-current={activeId === id ? "true" : undefined}
                    >
                      {label}
                    </a>
                  ))}
                </nav>
              </div>
            </div>

            <div className="d-none d-xl-block small text-muted mt-2">Tip: Use “Print” to save as PDF.</div>
          </div>
        </aside>

        {/* MAIN */}
        <main className="col-12 col-lg-8 col-xl-7">
          {/* A) CAP & PACELC */}
          <Section id="cap-pacelc" title="A) CAP, PACELC, and consistency models">
            <SayCallout>“I pick consistency per endpoint and per failure mode — not one global setting.”</SayCallout>

            <h3 className="h6 text-uppercase text-muted">CAP (only forces a choice during partitions)</h3>
            <ul className="mb-3">
              <li>
                <strong>P (partition):</strong> timeouts, packet loss, region split, long GC pauses that look like a split.
              </li>
              <li>
                Under <strong>P</strong>, you can’t guarantee both <strong>C</strong> (linearizable reads) and{" "}
                <strong>A</strong> (always respond successfully).
              </li>
              <li>
                Real design: choose which endpoints become “strict” (C) vs “serve something” (A) when P happens.
              </li>
            </ul>

            <h3 className="h6 text-uppercase text-muted">PACELC (adds the normal-day trade)</h3>
            <ul className="mb-3">
              <li>
                If <em>P</em> → choose <strong>A</strong> or <strong>C</strong>.
              </li>
              <li>
                Else → choose <strong>L</strong>atency or <strong>C</strong>onsistency (e.g., cross-region quorum adds RTT).
              </li>
            </ul>

            <h3 className="h6 text-uppercase text-muted">Consistency models you can name in interviews</h3>
            <ul className="mb-0">
              <li>
                <strong>Linearizable (strong):</strong> reads see the latest committed write globally.
              </li>
              <li>
                <strong>Sequential:</strong> everyone agrees on one order, but reads may not be “latest”.
              </li>
              <li>
                <strong>Causal:</strong> preserves “happens-before”; independent writes may be seen in different orders.
              </li>
              <li>
                <strong>Eventual / bounded staleness:</strong> replicas converge; may be stale for N seconds.
              </li>
              <li>
                <strong>Session guarantees:</strong> read-your-writes, monotonic reads/writes (amazing UX win).
              </li>
            </ul>

            <TemplateCard title="CAP/PACELC — template (what to say + how to decide)">
{`Checklist:
• Identify your “must be correct” invariants (money, permissions, uniqueness).
• Identify endpoints that can be stale (feeds, counters, recommendations).
• Decide behavior during partitions: “fail closed” (C) vs “serve stale” (A).
• Decide behavior when healthy: local low-latency (EL) vs quorum strong reads (EC).

Interview line:
“I’m PA/EL for feed reads and PC/EC for correctness paths like privacy + billing.”`}
            </TemplateCard>

            <ExampleCard title={`${feed.name} — CAP/PACELC choices`}>
{`Reads (feed timeline):
• Prefer Availability + low latency → serve from nearest region (PA/EL).
• Allow bounded staleness: ~${feed.freshness}.

Writes (privacy settings / blocks):
• Prefer Consistency → quorum write + read-your-writes (PC/EC).
• During partitions: fail closed (don’t accidentally expose private content).

Counters (likes/views):
• Eventual with merge/CRDT; accuracy within seconds is OK.`}
            </ExampleCard>
          </Section>

          {/* Optional in-article ad */}
          {ADS_ENABLED && (
            <div className="my-4 text-center d-print-none">
              <AdSlot enabled={ADS_ENABLED} adSlot="5201219465" style={{ display: "block", minHeight: 90 }} />
            </div>
          )}

          {/* B) Consensus */}
          <Section id="consensus" title="B) Consensus & coordination (Raft/Paxos, ZooKeeper/etcd)">
            <SayCallout>“Consensus is for the control plane — keep it small, critical, and boring.”</SayCallout>

            <h3 className="h6 text-uppercase text-muted">What consensus gives you</h3>
            <ul className="mb-3">
              <li>
                <strong>Single chosen leader</strong> (at a time) + <strong>quorum decisions</strong>.
              </li>
              <li>
                <strong>Replicated log</strong>: committed entries survive node failures.
              </li>
              <li>
                <strong>Safety</strong>: committed log entries aren’t “uncommitted” later (with correct Raft/Paxos).
              </li>
            </ul>

            <h3 className="h6 text-uppercase text-muted">Where to use (and where NOT to)</h3>
            <ul className="mb-0">
              <li>
                <strong>Use:</strong> leader election, distributed locks (with fencing), config/feature flags, membership metadata.
              </li>
              <li>
                <strong>Don’t use:</strong> storing high-QPS business data (it will bottleneck and hurt reliability).
              </li>
            </ul>

            <TemplateCard title="Consensus — template (how to explain it simply)">
{`• Pick Raft-backed systems (etcd/Consul/ZK) for coordination: leaders, locks, config.
• Keep payloads tiny and traffic low (control plane).
• Put business data in scalable stores/streams (data plane).
• Treat coordination like a dependency with strict SLOs and tight access controls.`}
            </TemplateCard>

            <ExampleCard title={`${feed.name} — what uses consensus`}>
{`Control plane:
• One “feed ranking config” source (feature flags, model version) stored in etcd.
• Leader-elected “scheduler” per region triggers backfills and refresh jobs.

Data plane:
• Posts/reactions/events flow through Kafka/Kinesis.
• Feed materialization data lives in partitioned storage (KV / wide-column / document), not etcd.`}
            </ExampleCard>
          </Section>

          {/* C) Leader election & fencing */}
          <Section id="leader-fence" title="C) Leader election & fencing tokens (split-brain protection)">
            <SayCallout>“Leader election without fencing is incomplete — old leaders must be rejected.”</SayCallout>

            <h3 className="h6 text-uppercase text-muted">The real problem: split brain</h3>
            <ul className="mb-3">
              <li>GC pause or flaky network can make an old leader “think” it’s still leader.</li>
              <li>Meanwhile a new leader is elected. Now two writers exist → corruption risk.</li>
            </ul>

            <h3 className="h6 text-uppercase text-muted">Fencing tokens (the fix)</h3>
            <ul className="mb-0">
              <li>
                Coordinator issues a <strong>monotonic token</strong> on leadership acquisition.
              </li>
              <li>
                Every side-effect must carry the token; downstream stores <strong>max token</strong> and rejects lower.
              </li>
            </ul>

            <CodeCard title="Fencing token — template pseudo-code">
{`token = coordinator.acquire_leadership()     # monotonic, e.g. 1051

while coordinator.lease_valid(token):
    write_to_downstream(token, payload)

# Downstream:
# if token < max_seen_token_for_shard: reject as stale`}
            </CodeCard>

            <TemplateCard title="Leader election + fencing — template">
{`• Use ephemeral nodes or leases for leader election.
• ALWAYS attach fencing tokens to side-effects:
  - DB writes (store max token)
  - queue publishes (consumers validate token)
  - object store updates (conditional writes)
• Prefer “reject stale writer” over “best effort”.`}
            </TemplateCard>

            <ExampleCard title={`${feed.name} — preventing split-brain fanout`}>
{`Scenario:
• Region scheduler A (token=1051) publishes “rebuild feed” tasks.
• A pauses (GC). Coordinator elects scheduler B (token=1052).
• A resumes and tries publishing again.

Protection:
• Consumers store max_seen_token per partition/user.
• Tasks from A (1051) are dropped; only B (1052) is accepted.
Result: no duplicate or corrupt fanout.`}
            </ExampleCard>
          </Section>

          {/* D) Clocks & ordering */}
          <Section id="clocks" title="D) Clocks & ordering (NTP, Lamport, vector clocks, HLC)">
            <SayCallout>“Ordering by wall-clock is a trap — order per key or via consensus.”</SayCallout>

            <h3 className="h6 text-uppercase text-muted">Clock types (what to use them for)</h3>
            <ul className="mb-3">
              <li>
                <strong>Wall clock (NTP):</strong> can jump backward/forward → fine for UI timestamps, risky for correctness.
              </li>
              <li>
                <strong>Monotonic clock:</strong> timeouts, deadlines, backoff (per-node only).
              </li>
              <li>
                <strong>Lamport clock:</strong> “happens-before” ordering without detecting concurrency.
              </li>
              <li>
                <strong>Vector clock:</strong> detects concurrent updates (metadata grows with participants).
              </li>
              <li>
                <strong>HLC:</strong> hybrid logical clock → near-time + monotonic, common in multi-region DBs.
              </li>
            </ul>

            <h3 className="h6 text-uppercase text-muted">Practical ordering rules</h3>
            <ul className="mb-0">
              <li>
                <strong>Total order</strong> needs consensus/log (Raft) or global sequencer.
              </li>
              <li>
                <strong>Per-entity order</strong> is easy: partition by entity key (order within partition).
              </li>
              <li>
                <strong>Don’t use timestamps</strong> for “latest wins” unless you control versioning (server-issued).
              </li>
            </ul>

            <TemplateCard title="Clocks & ordering — template">
{`• For correctness: avoid wall-time ordering; use consensus or server-issued versions.
• For streams: ordering is per partition → choose partition keys deliberately.
• For causality-sensitive merges: version vectors / HLC only where needed.
• For timeouts/retries: use monotonic clocks + deadlines, never wall clock.`}
            </TemplateCard>

            <ExampleCard title={`${feed.name} — ordering strategy`}>
{`Events:
• Partition “post events” by post_id → edits/deletes are ordered for a post.
• Partition “fanout tasks” by user_id → tasks for one user are ordered.

Versions:
• Use server-issued version numbers (or HLC) for “profile last updated” to avoid client clock skew.

Timeouts:
• Consumers use monotonic deadlines for processing + retries.`}
            </ExampleCard>
          </Section>

          {/* E) Conflict resolution & CRDTs */}
          <Section id="conflicts" title="E) Conflict resolution & CRDTs (LWW, merges, repair)">
            <SayCallout>“Pick conflict strategy per field: CRDT for intent, LWW only where safe.”</SayCallout>

            <h3 className="h6 text-uppercase text-muted">Conflict strategies</h3>
            <ul className="mb-3">
              <li>
                <strong>LWW (last-write-wins):</strong> simplest; risks losing intent under concurrent writes.
              </li>
              <li>
                <strong>CRDTs:</strong> converge via commutative/idempotent merges (great for counters/sets).
              </li>
              <li>
                <strong>App merges:</strong> field-wise rules (sum/max/union), with audit + repair.
              </li>
            </ul>

            <h3 className="h6 text-uppercase text-muted">Repair / anti-entropy</h3>
            <ul className="mb-0">
              <li>
                <strong>Read repair:</strong> fix divergence when you read (good for hot keys).
              </li>
              <li>
                <strong>Background repair:</strong> periodic reconciliation for cold keys.
              </li>
              <li>
                <strong>Merkle trees:</strong> compare hashes to find divergent ranges efficiently.
              </li>
            </ul>

            <TemplateCard title="Conflicts & CRDTs — template (how to choose)">
{`Checklist:
• Does the field represent “intent” from many writers? (likes, follows, device list)
  → prefer CRDT.
• Is the field “single-writer” or can be safely overwritten? (theme color, language)
  → LWW with server-issued version.
• Are merges domain-specific? (ranking signals, counters with caps)
  → app merge rules + audit.

Always:
• Document conflict rules per field.
• Add repair (read repair for hot, background repair for cold).`}
            </TemplateCard>

            <ExampleCard title={`${feed.name} — conflict choices by feature`}>
{`Reactions:
• Likes/emoji counts → PN-Counter CRDT (no lost increments across regions).

User lists:
• “Muted creators”, “hidden posts” → OR-Set CRDT (add/remove converge correctly).

Profile fields:
• “Bio”, “profile photo” → LWW with server-issued version to reduce skew issues.

Repair:
• Hot users: read-repair on access.
• Cold users: nightly background repair job.`}
            </ExampleCard>
          </Section>

          {/* Mini stance */}
          <Section id="mini-case" title="Mini stance: multi-region settings (a clean design story)">
            <SayCallout>“Reads stay local; strict invariants fail closed; everything else converges.”</SayCallout>

            <div className="row g-3">
              <div className="col-12 col-md-6">
                <h3 className="h6 text-uppercase text-muted">Design goals</h3>
                <ul className="mb-0">
                  <li>Low latency for user reads.</li>
                  <li>Correctness for privacy and identity invariants.</li>
                  <li>Clear conflict policy per field.</li>
                  <li>Operational clarity (observability + replay + repair).</li>
                </ul>
              </div>
              <div className="col-12 col-md-6">
                <h3 className="h6 text-uppercase text-muted">Toolbox</h3>
                <ul className="mb-0">
                  <li>Consensus for leaders/config.</li>
                  <li>Async replication cross-region.</li>
                  <li>CRDTs for intent-heavy sets/counters.</li>
                  <li>Versioned LWW for simple fields.</li>
                </ul>
              </div>
            </div>

            <TemplateCard title="Mini stance — template you can reuse">
{`“I split the world into:
1) Correctness-critical invariants → strong/quorum, fail closed during partitions.
2) UX-critical reads → local/available, accept bounded staleness.
3) Intent-heavy shared state → CRDTs to avoid lost updates.
Then I add fencing + repair + metrics to keep it safe in production.”`}
            </TemplateCard>

            <ExampleCard title={`${feed.name} — multi-region settings stance`}>
{`Privacy:
• Writes require quorum; during partitions, block writes rather than risk exposure.

Feed:
• Reads served from nearest region; staleness ~${feed.freshness} is OK.

State types:
• Counters/sets: CRDT.
• Simple preferences: LWW w/ server-issued versions.

Ops:
• Repair jobs + dashboards on divergence/conflicts + replay tools for streams.`}
            </ExampleCard>
          </Section>

          {/* Pitfalls */}
          <Section id="pitfalls" title="Common pitfalls & fixes (what interviewers love)">
            <ul className="mb-0">
              <li>
                <strong>Pitfall:</strong> “We’re strongly consistent” (everywhere).{" "}
                <strong>Fix:</strong> choose per endpoint; name invariants.
              </li>
              <li>
                <strong>Pitfall:</strong> wall-time used for correctness.{" "}
                <strong>Fix:</strong> server-issued versions, consensus, HLC where needed.
              </li>
              <li>
                <strong>Pitfall:</strong> leader election without fencing.{" "}
                <strong>Fix:</strong> monotonic tokens + downstream checks.
              </li>
              <li>
                <strong>Pitfall:</strong> “exactly-once” belief.{" "}
                <strong>Fix:</strong> idempotency keys + atomic commits + dedupe stores.
              </li>
              <li>
                <strong>Pitfall:</strong> no repair strategy.{" "}
                <strong>Fix:</strong> read-repair + background repair + monitoring.
              </li>
            </ul>

            <TemplateCard title="Pitfalls — template (quick close)">
{`“I avoid global strong claims. I define invariants, choose per-endpoint guarantees, add fencing for leaders,
use idempotency end-to-end, and back it with repair + metrics.”`}
            </TemplateCard>

            <ExampleCard title={`${feed.name} — pitfalls avoided`}>
{`• Don’t order global feed by timestamps across regions (clock skew).
• Don’t store feed content in etcd/ZooKeeper (control plane only).
• Expect duplicates; make consumers idempotent with dedupe keys.
• Always provide replay + DLQ + repair tooling.`}
            </ExampleCard>
          </Section>

          {/* One-liners */}
          <Section id="oneliners" title="One-liners for interviews (memorable, accurate)">
            <ul className="mb-0">
              <li>“CAP is about partitions; PACELC explains latency vs consistency on normal days.”</li>
              <li>“Consensus is for control-plane choices; data plane stays partitioned for throughput.”</li>
              <li>“Leader election needs fencing tokens; downstream must reject stale leaders.”</li>
              <li>“Ordering is per partition unless you pay for a sequencer or consensus log.”</li>
              <li>“CRDTs preserve user intent for counters/sets; LWW is for safe single-writer-ish fields.”</li>
            </ul>

            <TemplateCard title="One clean closing line (template)">
{`“For a Home Feed: PA/EL for reads, strict for privacy and billing, CRDTs for reactions, and fencing + repair for safety.”`}
            </TemplateCard>

            <ExampleCard title={`${feed.name} — 20-second answer`}>
{`“Reads stay local and fast; we tolerate bounded staleness. Privacy and invariants use quorum/strong consistency.
Events are ordered per key via partitioning. Counters and sets use CRDTs. Leaders use fencing tokens.
And we back it all with repair, replay, DLQs, and dashboards.”`}
            </ExampleCard>
          </Section>

          {/* Optional in-article ad near bottom */}
          {ADS_ENABLED && (
            <div className="my-4 text-center d-print-none">
              <AdSlot enabled={ADS_ENABLED} adSlot="5201219465" style={{ display: "block", minHeight: 90 }} />
            </div>
          )}
        </main>

        {/* RIGHT: Sponsored */}
        <aside className="d-none d-lg-block col-lg-4 col-xl-3 d-print-none">
          <div className="position-sticky" style={{ top: "1rem" }}>
            <RightSponsoredCard />
          </div>
        </aside>
      </div>
    </div>
  );
}
